import { useCallback, useEffect, useState } from "react";

/**
 * Tracks the reader's "call it" record on discovery pages.
 * Purely local (no account, no network) - it powers the mid-article
 * belief gate and the end-of-article status/share banner.
 */
const KEY = "sa.calls.v1";

type Store = {
  /** discovery slugs already answered, so a re-read doesn't re-lock */
  answered: Record<string, "real" | "fake">;
  correct: number;
  total: number;
  streak: number;
  bestStreak: number;
  day: string;
  today: number;
  /** last 10 outcomes, oldest first - powers the Wordle-style share grid */
  recent: boolean[];
  /** unused streak shields - earned by sharing the grid, spent automatically */
  shields: number;
  /** true when the last answer would have broken the streak but a shield ate it */
  shieldUsed: boolean;
};

const empty: Store = {
  answered: {},
  correct: 0,
  total: 0,
  streak: 0,
  bestStreak: 0,
  day: "",
  today: 0,
  recent: [],
  shields: 0,
  shieldUsed: false,
};


function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function read(): Store {
  if (typeof window === "undefined") return empty;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return empty;
    return { ...empty, ...(JSON.parse(raw) as Partial<Store>) };
  } catch {
    return empty;
  }
}

function write(s: Store) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota */
  }
}

/** Bet ids the reader has already called, so the feed never repeats a question. */
export function readAnsweredBetKeys(): Set<string> {
  const keys = Object.keys(read().answered ?? {});
  return new Set(keys.filter((k) => k.startsWith("bet:")).map((k) => k.slice(4)));
}


export function useGuessStreak(slug?: string) {
  const [store, setStore] = useState<Store>(empty);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setStore(read());
    setHydrated(true);
  }, []);

  const answeredHere = slug ? store.answered[slug] : undefined;

  /**
   * Shared streak math. A wrong call at streak >= 3 spends a shield (if any)
   * instead of resetting the run - the moment of peak investment is protected.
   */
  const advance = (prev: Store, correct: boolean): Store => {
    const day = todayStr();
    const sameDay = prev.day === day;
    const saveable = !correct && prev.streak >= 3 && prev.shields > 0;
    const streak = correct ? prev.streak + 1 : saveable ? prev.streak : 0;
    return {
      ...prev,
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
      streak,
      bestStreak: Math.max(prev.bestStreak, streak),
      day,
      today: (sameDay ? prev.today : 0) + 1,
      recent: [...(prev.recent ?? []), correct].slice(-10),
      shields: saveable ? prev.shields - 1 : prev.shields,
      shieldUsed: saveable,
    };
  };

  const record = useCallback(
    (choice: "real" | "fake", correct: boolean) => {
      if (!slug) return;
      setStore((prev) => {
        if (prev.answered[slug]) return prev;
        const next = advance(prev, correct);
        next.answered = { ...prev.answered, [slug]: choice };
        write(next);
        return next;
      });
    },
    [slug],
  );

  /** Feed bets have no slug - they key off the bet id so they never repeat. */
  const recordBet = useCallback((correct: boolean, betKey?: string) => {
    setStore((prev) => {
      const key = betKey ? `bet:${betKey}` : null;
      if (key && prev.answered[key]) return prev;
      const next = advance(prev, correct);
      if (key) next.answered = { ...prev.answered, [key]: correct ? "real" : "fake" };
      write(next);
      return next;
    });
  }, []);


  /** Earned by sharing the record grid - capped so it stays a real currency. */
  const grantShield = useCallback(() => {
    setStore((prev) => {
      if (prev.shields >= 2) return prev;
      const next = { ...prev, shields: prev.shields + 1 };
      write(next);
      return next;
    });
  }, []);

  return { ...store, hydrated, answeredHere, record, recordBet, grantShield };

}
